import { Box } from "@material-ui/core";
import { PCView, SPView } from "components/common/MediaQuery";
import {
  isStorefrontPasswordRequiredError,
  openShopifyCheckout,
} from "features/shopifyCheckout/openShopifyCheckout";
import { openLiveCommerceCheckoutWindow } from "features/shopifyLiveCommerce/customerAccountAuthorize";
import { prepareCheckoutUrl } from "features/shopifyLiveCommerce/prepareCheckoutUrl";
import { toShopifyCartLineInputs } from "features/shopifyLiveCommerce/syncCart";
import { useI18n } from "hooks/i18n/i18n";
import { streamingActions } from "modules/streaming";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AddedToCartToast } from "ui/Streaming/LiveCommerce/AddedToCartToast";

import { LiveCommerceFooter } from "./liveCommerce/LiveCommerceFooter";
import StreamingLiveCommerceDisplayInfo from "./liveCommerce/StreamingLiveCommerceDisplayInfo";
import StreamingLiveCommerceGoodsListV2 from "./liveCommerce/StreamingLiveCommerceGoodsListV2";
import { ErrorModal } from "./liveCommerce/StreamingLiveCommerceModal";
import { useShopifyLiveCommerce } from "./useShopifyLiveCommerce";

import type { ProductData } from "@spwn/types/firebase/firestore";

type CheckoutError = { caption: string; msg: string };

const closeWindow = (popup: Window | undefined): void => {
  try {
    if (popup !== undefined && !popup.closed) popup.close();
  } catch (error: unknown) {
    console.error(error);
  }
};

/**
 * Shopify の Checkout Kit で購入するライブコマース。
 *
 * 住所・決済・確認画面は Shopify 側に任せるので、商品一覧とフッターだけを持つ。
 * 商品・カート・checkoutUrl は useShopifyLiveCommerce が Redux を通さずに扱う。
 */
const StreamingLiveCommerceShopify: React.FC<{
  THEOPlayerElementHeight: number;
}> = ({ THEOPlayerElementHeight }) => {
  const {
    eventId,
    goods,
    cartItems,
    cartTotalNum,
    checkoutUrl,
    incrementCartItem,
    decrementCartItem,
    addToCart,
    addToCartToBuy,
    setSelectType,
    clearAfterPurchase,
  } = useShopifyLiveCommerce();

  const { t } = useI18n();
  const dispatch = useDispatch();

  const [showToast, setShowToast] = useState(false);
  const [goodsInfo, setGoodsInfo] = useState<ProductData | null>(null);
  const [checkoutError, setCheckoutError] = useState<CheckoutError | null>(
    null
  );
  // 認可の間に足された商品も同期できるよう、同期の直前に最新のカートを読む
  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;
  // 認可からチェックアウトまでの途中の購入。並行して走らせると、保存した state や
  // code challenge を上書きし合い、同じ code を両方が受け取ってしまう
  const inFlightRef = useRef<{
    popup: Window;
    controller: AbortController;
  } | null>(null);

  useEffect(() => {
    if (!showToast) return;
    const timer = setTimeout(() => {
      setShowToast(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [showToast]);

  // 閉じるときは、待っている認可を打ち切る
  useEffect(
    () => () => {
      inFlightRef.current?.controller.abort();
    },
    []
  );

  // 親は isPurchased を見て閉じる。開き直したときに残らないよう、閉じるときに戻す
  useEffect(
    () => () => {
      dispatch(streamingActions.successPurchaseLiveCommerce(false));
    },
    [dispatch]
  );

  const showCheckoutError = (msg: string) => {
    setCheckoutError({
      caption: t("streaming.commerceModal.errorHeading"),
      msg,
    });
  };

  /** popup を渡すと、開けなかったときにその窓も閉じる（空の窓を残さない） */
  const openCheckout = (url: string, target?: string, popup?: Window) => {
    try {
      openShopifyCheckout({
        checkoutUrl: url,
        target,
        onComplete: () => {
          clearAfterPurchase();
          dispatch(streamingActions.successPurchaseLiveCommerce(true));
        },
        onError: (event) => {
          // 運営者向けの詳しい原因（パスワード保護など）はログに残し、画面には出さない
          console.error("Shopify checkout error", event.detail);
          showCheckoutError(
            isStorefrontPasswordRequiredError(event)
              ? t("streaming.commerceModal.storefrontPasswordRequired")
              : t("streaming.commerceModal.shopifyCheckoutError")
          );
        },
      });
    } catch (error: unknown) {
      console.error(error);
      closeWindow(popup);
      showCheckoutError(t("streaming.commerceModal.shopifyCheckoutError"));
    }
  };

  const handleCheckout = () => {
    if (
      cartTotalNum === 0 ||
      checkoutUrl === undefined ||
      eventId === undefined
    ) {
      return;
    }

    // 認可の途中なら、その窓を前に出すだけにする
    const { current } = inFlightRef;
    if (current !== null) {
      if (!current.popup.closed) {
        current.popup.focus();
        return;
      }
      // 窓が閉じて見える（閉じられたか、COOP で関係が切れた）なら、前の待ちは捨てる
      current.controller.abort();
    }

    // popup はユーザー操作の中でしか開けない。await より前に窓を開いておき、
    // 認可からチェックアウトまで同じ窓を使い回す
    const opened = openLiveCommerceCheckoutWindow();
    if (opened === null) {
      openCheckout(checkoutUrl);
      return;
    }
    const { popup, name } = opened;
    const controller = new AbortController();
    inFlightRef.current = { popup, controller };

    void prepareCheckoutUrl({
      popup,
      eventId,
      getLines: () => toShopifyCartLineInputs(cartItemsRef.current),
      fallbackUrl: checkoutUrl,
      signal: controller.signal,
    })
      .then((prepared) => {
        if (prepared.kind === "cancelled") {
          closeWindow(popup);
          return;
        }
        if (prepared.kind === "detached") {
          // ログインは済んでいて、トークンも保存してある
          setCheckoutError({
            caption: t("streaming.commerceModal.loginCompleted"),
            msg: t("streaming.commerceModal.loginCompletedPurchaseAgain"),
          });
          return;
        }
        // 閉じられた窓の名前で開くと、新しい窓として扱われてブロックされる
        if (popup.closed) return;
        try {
          // 他サイトを経由すると名前が消えることがある。同じ生成元に戻っていれば付け直せる
          popup.name = name;
        } catch (error: unknown) {
          console.error(error);
        }
        openCheckout(prepared.url, name, popup);
      })
      .finally(() => {
        if (inFlightRef.current?.controller === controller) {
          inFlightRef.current = null;
        }
      });
  };

  const footerProps = {
    // 窓を開けなかったときに開く同期済みの checkoutUrl が要るので、揃うまで押せない
    isDisabled: cartTotalNum === 0 || checkoutUrl === undefined,
    itemNum: cartTotalNum,
    pseudoCart: cartItems,
    buttonLabel: t("streaming.commerceCart.purchase"),
    onClickButton: handleCheckout,
    decrementGoodsCount: decrementCartItem,
    incrementGoodsCount: incrementCartItem,
  };

  if (checkoutError !== null) {
    return (
      <ErrorModal
        caption={checkoutError.caption}
        msg={checkoutError.msg}
        closeModal={() => setCheckoutError(null)}
      />
    );
  }

  if (goodsInfo !== null) {
    return (
      <StreamingLiveCommerceDisplayInfo
        goodsInfo={goodsInfo}
        THEOPlayerElementHeight={THEOPlayerElementHeight}
        closeGoodsInfo={() => setGoodsInfo(null)}
      />
    );
  }

  return (
    <React.Fragment>
      <StreamingLiveCommerceGoodsListV2
        THEOPlayerElementHeight={THEOPlayerElementHeight}
        pseudoCart={cartItems}
        selectedLiveCommerceGoods={goods}
        setType={setSelectType}
        addToCart={(classGoodsList) => {
          addToCart(classGoodsList);
          setShowToast(true);
        }}
        addToCartToBuy={addToCartToBuy}
        openGoodsInfo={setGoodsInfo}
        // カートは一覧のフッターに常に出ているので、別画面には遷移しない
        openLiveCommerceCart={() => undefined}
        incrementCartItem={incrementCartItem}
        decrementCartItem={decrementCartItem}
      />

      <AddedToCartToast showToast={showToast} />

      <PCView>
        <Box position={"absolute"} bottom={0} width={"100%"} zIndex={"10"}>
          <LiveCommerceFooter {...footerProps} />
        </Box>
      </PCView>

      <SPView>
        <Box
          position={"fixed"}
          bottom={0}
          left={0}
          width={"100%"}
          zIndex={"10"}
        >
          <LiveCommerceFooter {...footerProps} />
        </Box>
      </SPView>
    </React.Fragment>
  );
};

export default StreamingLiveCommerceShopify;
